import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import GeneratedAvatar from "@/components/common/GeneratedAvatar";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";
import { Megaphone, Target, TrendingUp, Users, MapPin, Plus } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fmtCurrency } from "@/lib/formatters";
import { listAdminSalesAgents } from "@/lib/api";

export default function SalesPipeline() {
  const { currency, currentAdmin } = useApp();
  const canManageAgents = currentAdmin?.permissions?.includes("sales.manage");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-sales-agents"],
    queryFn: ({ signal }) => listAdminSalesAgents({ limit: 100 }, signal),
  });
  const agents = useMemo(() => (data?.items || []).map((agent) => ({
    ...agent,
    name: agent.fullName,
    activeLeads: 0,
    conversion: agent.conversionRate,
    demosCompleted: 0,
    revenueGenerated: agent.revenueMinor / 100,
    weeklyAchieved: 0,
  })), [data?.items]);

  const stats = useMemo(() => {
    const acquired = agents.reduce((s, a) => s + a.shopsAcquired, 0);
    const revenue = agents.reduce((s, a) => s + a.revenueGenerated, 0);
    const activeLeads = agents.reduce((s, a) => s + a.activeLeads, 0);
    const conversion = agents.length ? Math.round(agents.reduce((s, a) => s + a.conversion, 0) / agents.length) : 0;
    return { acquired, revenue, activeLeads, conversion };
  }, [agents]);

  return (
    <div data-testid="sales-page">
      <PageHeader
        eyebrow="Field marketing"
        title="Sales pipeline"
        subtitle="Track field agents going shop-to-shop, manage leads from first contact to paid customer."
        actions={canManageAgents ? <Button asChild className="bg-[#0E5B52] hover:bg-[#0B4A43]"><Link to="/sales/agents/new"><Plus className="mr-2 h-4 w-4" />New sales agent</Link></Button> : null}
      />

      {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error.message}</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="kpi-acquired" label="Shops acquired" value={stats.acquired} icon={Megaphone} accent="#A7F3D0" />
        <KpiCard testid="kpi-active-leads" label="Active leads" value={stats.activeLeads} icon={Target} accent="#FDE047" />
        <KpiCard testid="kpi-avg-conv" label="Avg conversion" value={`${stats.conversion}%`} icon={TrendingUp} accent="#BAE6FD" />
        <KpiCard testid="kpi-agent-revenue" label="Revenue generated" value={fmtCurrency(stats.revenue, currency)} icon={Users} accent="#D9F99D" />
      </div>

      {/* Agents row */}
      <div className="card-elev rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.08em] font-medium text-[#6B7280]">Field team</div>
            <div className="font-display text-base font-semibold text-[#064E3B] mt-1">Sales agents</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {isLoading && Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-52 rounded-xl" />)}
          {agents.map((a) => (
            <Sheet key={a.id}>
              <SheetTrigger asChild>
                <button
                  data-testid={`agent-card-${a.id}`}
                  onClick={() => setSelectedAgent(a)}
                  className="text-left p-4 rounded-xl border border-[#0E5B52]/10 hover:border-[#0E5B52]/30 hover:bg-[#F0F6F1]/40 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <GeneratedAvatar seed={a.id} name={a.name} className="h-10 w-10" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#064E3B] truncate">{a.name}</div>
                      <div className="text-[11px] text-[#4B5563] flex items-center gap-1"><MapPin className="w-3 h-3" />{a.territory}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[11px] text-[#4B5563]">
                    <span>Weekly target {a.weeklyAchieved}/{a.dailyTarget * 7}</span>
                    <span className="font-semibold text-[#0E5B52]">{a.conversion}% conv</span>
                  </div>
                  <Progress value={(a.weeklyAchieved / (a.dailyTarget * 7)) * 100} className="h-1.5 mt-2 bg-[#F0F6F1]" />
                  <div className="mt-3 flex items-center gap-2">
                    <span className="pill" style={{ background: "#F0F6F1", color: "#0E5B52" }}>{a.shopsAcquired} shops</span>
                    <span className="pill font-mono" style={{ background: "#FDE047", color: "#92400E" }}>{a.referralCode}</span>
                  </div>
                </button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                {selectedAgent && selectedAgent.id === a.id && (
                  <>
                    <SheetHeader>
                      <SheetTitle>{a.name}</SheetTitle>
                      <SheetDescription>{a.territory} · Code {a.referralCode}</SheetDescription>
                    </SheetHeader>
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      <Mini label="Shops acquired" value={a.shopsAcquired} />
                      <Mini label="Demos done" value={a.demosCompleted} />
                      <Mini label="Conversion" value={`${a.conversion}%`} />
                      <Mini label="Active leads" value={a.activeLeads} />
                      <Mini label="Daily target" value={a.dailyTarget} />
                      <Mini label="Weekly achieved" value={a.weeklyAchieved} />
                    </div>
                    <div className="kente-divider mt-5 mb-5" />
                    <div className="text-xs uppercase tracking-wider text-[#4B5563] mb-2">Revenue generated</div>
                    <div className="font-display text-2xl font-semibold text-[#064E3B]">{fmtCurrency(a.revenueGenerated, currency)}</div>
                  </>
                )}
              </SheetContent>
            </Sheet>
          ))}
          {!isLoading && agents.length === 0 && (
            <div className="col-span-full flex min-h-52 flex-col items-center justify-center rounded-xl bg-[#F4F9F5] px-6 text-center">
              <Users className="mb-3 h-7 w-7 text-[#0E5B52]/60" />
              <div className="font-semibold text-[#0F2A26]">No sales agents yet</div>
              <p className="mt-1 max-w-sm text-sm text-[#6B7280]">Create the first field agent to begin tracking territory performance and referral attribution.</p>
              {canManageAgents && <Button asChild size="sm" className="mt-4 bg-[#0E5B52] hover:bg-[#0B4A43]"><Link to="/sales/agents/new"><Plus className="mr-2 h-4 w-4" />Create sales agent</Link></Button>}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="p-3 rounded-xl bg-[#F0F6F1] border border-[#0E5B52]/10">
      <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">{label}</div>
      <div className="font-display text-lg font-bold text-[#064E3B] mt-0.5">{value}</div>
    </div>
  );
}
